import {
  differenceInHours,
  differenceInMinutes,
  format,
  isThisYear,
  isToday,
  isYesterday,
} from 'date-fns';
import { format as formatTz, toZonedTime } from 'date-fns-tz';

export const formatEventTime = (dateString: string): string => {
  const date = new Date(dateString);
  const timeZone = 'America/New_York';
  const zonedDate = toZonedTime(date, timeZone);
  return formatTz(zonedDate, 'h:mm a', { timeZone });
};

export const formatFullDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return format(date, "MMM d, yyyy 'at' h:mm a");
};

/**
 * Smart compact time formatting:
 * - < 1 hour: "5m"
 * - < 24 hours: "3h"
 * - Today: "1:30 PM"
 * - Yesterday: "Yesterday"
 * - This year: "Oct 24"
 * - Older: "Oct 24, 2023"
 */
export const formatSmartTime = (
  dateString: string,
  now: Date = new Date(),
): string => {
  const date = new Date(dateString);

  const diffInMinutes = differenceInMinutes(now, date);

  if (diffInMinutes < 1) {
    return 'Just now';
  }

  if (diffInMinutes < 60) {
    return `${diffInMinutes}m`;
  }

  const diffInHours = differenceInHours(now, date);
  if (diffInHours < 24 && isToday(date)) {
    return format(date, 'h:mm a');
  }

  if (isToday(date)) {
    return format(date, 'h:mm a');
  }

  if (isYesterday(date)) {
    return 'Yesterday';
  }

  if (isThisYear(date)) {
    return format(date, 'MMM d');
  }

  return format(date, 'MMM d, yyyy');
};

/**
 * Formats a deepdiff path key into a human readable label
 * e.g. "root['date_of_loss']" -> "Date of Loss"
 * e.g. "root['address']['city']" -> "Address > City"
 */
export const formatDiffKey = (key: string): string => {
  // Remove "root" prefix if present
  const cleanKey = key.replace(/^root/, '');

  // Extract all array/bracket accessors: ['field'] -> field
  const matches = [...cleanKey.matchAll(/\['([^']+)'\]/g)];
  const parts = matches.map((m) => m[1]);

  // If no parts found (e.g. simple string key), just use the key
  if (parts.length === 0) {
    parts.push(key);
  }

  return parts
    .map((part) => {
      // Replace underscores with spaces and title case
      return part.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    })
    .join(' › ');
};

/**
 * Formats a value for display in a diff
 */
export const formatDiffValue = (val: unknown): string => {
  if (val === null || val === undefined) return 'Empty';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
};
